/**
 * The overview: five KPIs and the clearance lanes. It answers "which groups are the
 * problem" before anyone commits to opening one.
 *
 * THE PER-GROUP CHARTS ARE ENTITY-ONLY (19 Aug 2026, Mike). At Group, thirteen small
 * multiples from two estates that share no machines said what the lanes already say —
 * which groups never clear — at ten times the height. At one entity they are six or seven
 * charts of one estate, which is a comparison worth having, so they render there. See Grid.
 *
 * The explainer above the lanes changes with the view because the two encodings mean
 * different things, and the levelled one needs saying out loud: a full bar is not "100%
 * utilised", it is "there is more work than hours".
 *
 * IT ALSO OWNS THE EMPTY STATE, and that is the point of the split below. A scope with no
 * scheduled-load model still has a served answer on this page: trailing utilisation comes
 * from /utilisation/availability, a different feed with a different spine, and Oracle has
 * carried it since before Kirkstall could. The surface used to refuse the whole page ahead
 * of this component, so scoping to Oracle blanked the one Oracle capacity number that is
 * built. A missing load model now suppresses the four load KPIs and the resource cards,
 * and nothing else.
 */

import React from 'react'
import ClearanceLanes from './ClearanceLanes'
import KpiRow from './KpiRow'
import Legend from './Legend'
import ProcessStrip from './ProcessStrip'
import ResourceGrid from './ResourceGrid'
import UtilisationCard from './UtilisationCard'
import UtilisationDisclosure from './UtilisationDisclosure'
import type { SurfaceData } from '../../hooks/useLoadingSurface'
import type { Site } from '../../types/metrics'
import { hrs } from './format'

function explainer(levelled: boolean, overdue: number): string {
  if (levelled) {
    return (
      'Levelled — the bar is always full because there is more work than hours. Red is capacity ' +
      'spent on late work and its DEPTH is how late: deepest where the oldest arrears are being ' +
      'worked off, fading as the queue ages out. A tall red block means the week buys you nothing ' +
      'new; the dashed outline above the line is what is still queued behind it. Click any ' +
      'resource, or any single week, to open it.'
    )
  }
  return (
    'As scheduled — every works order sits in the week its delivery date says it should start, so ' +
    `the first column carries all ${hrs(overdue)} of arrears and reads high by design. Click any ` +
    'resource to see when it actually gets done.'
  )
}

function Note({ head, body }: { head: string; body: string }): React.JSX.Element {
  return (
    <p className="px-[22px] pt-2.5 font-sans text-[11px]" style={{ color: 'var(--lc-mut)' }}>
      <b>{head}</b> {body}
    </p>
  )
}

/**
 * The entities in scope that served no load, named rather than dropped.
 *
 * `alone` says they are the WHOLE scope, and the sentence must change with it. Beside
 * another site's rows the point is what the page OMITS. On its own the point is what the
 * page still SERVES — otherwise the reader takes an empty grid for an empty estate.
 *
 * IT NO LONGER NAMES A CAUSE. It used to read "the backward-schedule spine is Factory Master
 * only", which was true while Oracle had no load model and became false the day it got one
 * (19 Aug 2026). A caveat that outlives its reason is worse than no caveat, because nothing
 * on screen tells the reader it expired. The backend's own refusal detail carries the reason.
 */
function Unserved({ data, alone }: { data: SurfaceData; alone: boolean }): React.JSX.Element | null {
  if (data.unserved.length) {
    const why = data.unserved.map((e) => e.reason).join(' · ')
    return (
      <Note
        head={data.unserved.map((e) => e.site).join(', ')}
        body={
          `serves no scheduled load here (${why}). ` +
          (alone
            ? 'Trailing utilisation below is served for this scope: it reads a different feed.'
            : 'Nothing else on this page includes it.')
        }
      />
    )
  }
  if (!alone) return null
  // Served, but the window holds no machine resource. A DIFFERENT emptiness from an
  // unserved entity, and it must not borrow that sentence.
  return (
    <Note
      head="No machine resource carries load in this window."
      body={data.entities.map((e) => e.error?.message ?? 'no rows served').join(' · ')}
    />
  )
}

/** The utilisation card with no forward-load KPIs beside it. Same grid as KpiRow so the
 *  card keeps its size and place: this is the row minus four cells, not a new component. */
function SoleUtilisation({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <div className="grid grid-cols-2 items-stretch gap-2.5 px-[22px] pt-3.5 md:grid-cols-3 xl:grid-cols-5">
      {children}
    </div>
  )
}

/** Everything that needs a scheduled-load model to mean anything. */
function Load({ data, site, levelled, onOpen }: {
  data: SurfaceData
  site: Site
  levelled: boolean
  onOpen: (id: string, week: number) => void
}): React.JSX.Element {
  return (
    <>
      <p
        className="mx-[22px] mt-4 rounded-[9px] border px-[13px] py-2.5 font-sans text-[12px] leading-[1.5]"
        style={{ background: 'var(--lc-explainer)', borderColor: 'var(--lc-b07)', color: 'var(--lc-ink3)' }}
      >
        {explainer(levelled, data.kpis.overdue)}
      </p>
      <ClearanceLanes
        resources={data.resources}
        scopeName={site === 'Group' ? 'whole group' : site}
        banded={site === 'Group'}
        onOpen={(id) => onOpen(id, 0)}
      />
      <ProcessStrip groups={data.process} hours={data.processHours} />
      {site !== 'Group' && <ResourceGrid data={data} levelled={levelled} onOpen={onOpen} />}
    </>
  )
}

/** The SCOPE chips left this row on 19 Aug 2026: entity is declared once, in the page's
 *  ScopeBar. Two switches for one axis is how a reader learns to distrust both. */
export default function Overview({ data, site, levelled, from, to, onOpen }: {
  data: SurfaceData
  site: Site
  levelled: boolean
  from: string
  to: string
  onOpen: (id: string, week: number) => void
  /** Load confidence: the CHIP rides the legend row and the PANEL opens under it. It is
   *  information about the numbers below, not a filter on them, so it sits with the key
   *  that reads them rather than in the page's scope bar (19 Aug 2026, Mike). */
}): React.JSX.Element {
  const [utilOpen, setUtilOpen] = React.useState(false)
  const load = data.resources.length > 0
  const util = (
    <UtilisationCard site={site} from={from} to={to} open={utilOpen} onToggle={() => setUtilOpen((v) => !v)} />
  )
  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-[22px] px-[22px] pt-4">
        {load ? <Legend levelled={levelled} where="overview" /> : <span />}
      </div>
      <Unserved data={data} alone={!load} />
      {load ? <KpiRow kpis={data.kpis} weeks={data.weeks.length}>{util}</KpiRow> : <SoleUtilisation>{util}</SoleUtilisation>}
      {utilOpen && <UtilisationDisclosure site={site} from={from} to={to} />}
      {load && <Load data={data} site={site} levelled={levelled} onOpen={onOpen} />}
    </div>
  )
}
